import { ACCESS } from '../data/access.js';
import { floorOf, onRung } from './standing.js';

/**
 * Who is allowed where, read off standing.
 *
 * One function and one comparison. What it is *not* is the interesting part:
 * there is no inventory, no pickup, nothing to lose and nothing to find. The
 * question "can I go in there" has exactly the same answer as "what do they
 * think of me", which is the point — it makes every write-up cost something
 * concrete and every favour buy something concrete, in the same currency.
 *
 * Granted and withdrawn are both events, because losing access matters more
 * than gaining it and a door that quietly re-locks is a door that feels broken.
 *
 * A rule names a rung, not a number. It used to name four different numbers —
 * eighteen, twenty, twenty-five, fifty-five — which are four facts nobody can
 * hold and which read, from inside the game, as a door that opens at some point
 * for some reason. Rungs are the whole of what standing unlocks, so the doors
 * are on them: warm gets you the marble, the apron and the walk-in; trusted
 * gets you his office.
 */
/*
 * `working` is the one other answer, and it is not standing. A job can send
 * you somewhere: the apron belongs to whoever is working it, and somebody
 * towing a struck lawn up to the dock is working it for as long as the job is
 * theirs. Asked rather than held, so it goes when the job does, and it opens
 * nothing the job does not name.
 */
export function createAccess({ standing, rules = ACCESS, working = () => false }) {
  /** What was true last time anybody asked, so changes can be announced. */
  const was = new Map();

  function allows(id) {
    const rule = rules[id];
    if (!rule) return true;
    if (working(id)) return true;
    return onRung(standing.get(rule.department), rule.rung);
  }

  return {
    allows,
    ruleFor: (id) => rules[id] ?? null,

    /** How far off you are, for anything that wants to say so. */
    shortBy(id) {
      const rule = rules[id];
      if (!rule) return 0;
      return Math.max(0, floorOf(rule.rung) - standing.get(rule.department));
    },

    /**
     * Everything that has changed hands since the last call.
     *
     * Polled rather than pushed, because standing moves for a dozen reasons a
     * shift and none of them should have to know that doors exist.
     */
    changes() {
      const out = [];
      for (const id of Object.keys(rules)) {
        const now = allows(id);
        if (was.has(id) && was.get(id) === now) continue;
        const first = !was.has(id);
        was.set(id, now);
        if (first) continue;
        out.push({ id, allowed: now, rule: rules[id] });
      }
      return out;
    },

    /** Seeds the record without announcing anything. Used on load. */
    settle() {
      for (const id of Object.keys(rules)) was.set(id, allows(id));
    },

    clear() {
      was.clear();
    },
  };
}
